/**
 * Typed queries and commands over the contract types. Two rules live here:
 * every query key is engagement-scoped (C-004 — a cross-engagement read
 * cannot be issued by accident), and the count and the landing list share one
 * key (design promotions 010/026) — the nav badge and Home's list are two
 * consumers of pendingCandidatesQuery, so they cannot disagree.
 */
import { keepPreviousData, queryOptions } from '@tanstack/react-query'
import { apiFetch } from '../auth/apiFetch'
import type {
  AddStakeholderRequest, AddStakeholderResponse, CorrectStakeholderResponse,
} from './types'
import type { Company, EngagementCategoryInput, EngagementPhaseInput, MembershipBindResponse } from './types'
import type { EngagementPlatformRequest, ProductOption } from './types'
import type {
  ServiceNowConnection,
  ServiceNowConnectionRequest,
  ServiceNowCredentialRequest,
  ServiceNowIdentity,
  ServiceNowScheduleRequest,
  ServiceNowTableDetail,
  ServiceNowTablePage,
} from './types'
import type { ProductCatalog, ProductSearchRequest, ProductSearchResult } from './types'
import type { FeedbackBody, FeedbackReceipt } from './types'
import type { TodayEntry,
  StaleItem,
  Baseline,
  BaselinePlan,
  CriticalPath,
  AgentRunTrace,
  ApproveSetRequest,
  ApproveSetResponse,
  BirthSpecs,
  ContextEntry,
  DismissedHomeProposal,
  DismissSetResponse,
  DocumentRow,
  AgreeOutcomeResponse,
  ColdStartDraftResponse,
  ColdStartProgress,
  DraftChangeResponse,
  EngagementDirectoryEntry,
  EvalRunReport,
  Indicator,
  IndicatorReadings,
  NudgeTray,
  OutputLibraryEntry,
  WatchedOutcome,
  LedgerEntry,
  Me,
  NeedsWorkResponse,
  PendingChangeSet,
  PendingIntakeQuestion,
  PlanNode,
  HistoryLine,
  RecordAskRow,
  RestoreSetResponse,
  ResolveQuestionResponse,
  SetOwnerResponse,
  RunSummary,
  Stakeholder,
  StatusReportGenerated,
  StatusReportVersion,
  StatusReportVersionView,
  UploadResponse,
  WorkItemView,
  EngagementProfile,
  CreateEngagementResponse,
  MembershipGrantResponse,
  MembershipRevokeResponse,
  MembershipRoleResponse,
  EngagementMember,
} from './types'

// The session, on the client's default freshness rather than cached for the
// life of the tab. It was `Infinity`, which was right while nothing about a
// signed-in person changed mid-session. Memberships and roles now do (added,
// removed, role changed by an administrator), and the role Work shows is read
// from here, so a stale copy says the wrong thing until a reload.
export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: () => apiFetch<Me>('/me'),
})

/*
  Settings › You (wave 23): the name a person appears under and the time zone
  their day is counted in, kept on the server. A field left undefined is left as
  it is; an empty name goes back to the sign-in name. Answers the new `/me`.
*/
export const updateProfile = (profile: { name?: string; timeZone?: string }) =>
  apiFetch<Me>('/me/profile', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: profile.name ?? null, timeZone: profile.timeZone ?? null }),
  })

// The engagement directory (wave-4 step 7): the switcher's frame — id + name
// only, platform-plane by design (the wall governs content, not the frame).
// Also what the shell reconciles the active engagement against, so it runs on
// the default freshness too: somebody added while signed in sees the new
// engagement on their next focus instead of up to five minutes later.
export const engagementsQuery = queryOptions({
  queryKey: ['engagements'],
  queryFn: () => apiFetch<EngagementDirectoryEntry[]>('/engagements'),
})

// The birth contracts the review card renders (ADR-0036 §3) — registry data
// served by the API, so the card cannot drift from the code. Changes by
// deploy, never by use: cache for the session.
export const birthSpecsQuery = queryOptions({
  queryKey: ['birth-specs'],
  queryFn: () => apiFetch<BirthSpecs>('/birth-specs'),
  staleTime: Infinity,
})

/*
  The work items behind a lens.

  `mine` is resolved SERVER-SIDE from the token, never here: C-035 puts the
  work layer's wall at the query layer, "not filtered in a view, not omitted by
  a renderer". A client-side `.filter(i => i.assignees.includes(me))` would look
  identical on screen and would have shipped every other person's rows to the
  browser to do it.

  The lens is part of the query key, so the two lenses cache separately and
  switching does not show one lens's rows under the other's heading.
*/
/*
  Today, served (ADR-0050): everything that needs the signed-in person today,
  banded and ordered by the server. The screen and the chat read this one list,
  so the two can never disagree about what is due.
*/
/**
 * Today, as the server ranks it (ADR-0050).
 *
 * `who` is the PM view's scope (ruled 2026-09-22): unset is your own, and
 * `everyone` or a member's object id is somebody else's. It is part of the key,
 * so switching the selector reads the list for that scope rather than showing
 * the last one while it loads.
 */
export const todayQuery = (engagementId: string, who?: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'today', who ?? 'me'],
    queryFn: () =>
      apiFetch<{ entries: TodayEntry[]; moved: TodayEntry[] }>(
        `/engagements/${engagementId}/today${who ? `?who=${encodeURIComponent(who)}` : ''}`),
  })

/*
  "NOT TODAY", AND "PULL BACK IN" (ruled by Erik 2026-09-22). A disposition on
  the person's own list, never on the record: nobody else's Today moves. The day
  it comes back is the server's, in the person's zone, so nothing here sends a
  date.
*/
export const deferToday = (engagementId: string, entryKey: string, reason: string | null) =>
  post<void>(`/engagements/${engagementId}/today/defer`, { entryKey, reason })

export const restoreToday = (engagementId: string, entryKey: string) =>
  post<void>(`/engagements/${engagementId}/today/restore`, { entryKey })

export const workItemsQuery = (engagementId: string, lens: 'all' | 'mine' = 'all') =>
  queryOptions({
    queryKey: ['eng', engagementId, 'work-items', lens],
    queryFn: () =>
      apiFetch<WorkItemView[]>(`/engagements/${engagementId}/work-items?lens=${lens}`),
  })

// The same key Home's set list and the chat's reviews render (the 010/026
// binding rule): the rail count, Home's rows, and the in-session review all
// read one query and cannot disagree. Polling is the refresh mechanism for
// async arrivals (intake sets off the worker's queue); SSE arrives with the
// chat/streaming work.
/**
 * Create task (G-44). An action is not a task: it lands as a work item and
 * becomes a task only by an explicit, attributed promotion. This DRAFTS - the
 * task is a plan record and arrives as a candidate for approval on Home, per
 * C-033 and the routing law.
 */
export const promoteWorkItem = (engagementId: string, workItemId: string) =>
  apiFetch<{ setId: string; taskId: string }>(
    `/engagements/${engagementId}/work-items/${workItemId}/promote`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
  )

const post = <T,>(path: string, body: unknown) =>
  apiFetch<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

/**
 * Multipart POST. No Content-Type header, deliberately: the browser sets it
 * with the boundary, and setting it by hand produces a body the server cannot
 * parse. Same shape the document upload uses.
 */
const postForm = <T,>(path: string, form: FormData) =>
  apiFetch<T>(path, { method: 'POST', body: form })

/*
  A work item's DIRECT axes (ADR-0031 Amendment §1): who does it, and its own
  due date. Outside the lock, so no change set; each write is attributed to the
  caller. The chat proposes them and a person's Accept calls these (wave 23).
  Assignees are the WHOLE list: the caller adds to what the item already has.
*/
export const setWorkItemAssignees = (engagementId: string, workItemId: string, assignees: string[]) =>
  post<WorkItemView>(`/engagements/${engagementId}/work-items/${workItemId}/assignees`, { assignees })

export const setWorkItemDue = (engagementId: string, workItemId: string, due: string | null) =>
  post<WorkItemView>(`/engagements/${engagementId}/work-items/${workItemId}/due`, { due })

export const addWorkStep = (engagementId: string, workItemId: string, what: string) =>
  post<WorkItemView>(`/engagements/${engagementId}/work-items/${workItemId}/steps`, { what })

/** One step, on its own. There is deliberately no bulk arm (022 §3). */
export const resolveWorkStep = (
  engagementId: string,
  workItemId: string,
  stepId: string,
  kind: string,
  reason?: string,
) =>
  post<WorkItemView>(
    `/engagements/${engagementId}/work-items/${workItemId}/steps/${stepId}/resolve`,
    { kind, reason },
  )

/**
 * Refuses with 409 and the open items while work remains underneath (C-036).
 * The caller renders the tray from `open` rather than guessing what is wrong.
 */
/**
 * Complete, and the same act choosing which outputs go up to the task (G-22).
 * One call, because it is one act: there is deliberately no separate promote-
 * the-outputs request for the completion to race with.
 */
export const completeWorkItem = (
  engagementId: string, workItemId: string, sendUp?: string[],
) =>
  post<WorkItemView>(
    `/engagements/${engagementId}/work-items/${workItemId}/complete`,
    { sendUp },
  )

/*
  Outputs — the thing the work produces (ADR-0031 §7, brief 17, canvas 1f).

  Drafting is free: C-033 puts an output draft's body outside the change
  machinery by name, so none of these go through the drafting door. Publishing
  is the attributed gate, and it refuses with 409 + `says` rather than by
  disabling anything.
*/
export const addOutput = (
  engagementId: string, workItemId: string, name: string, format?: string,
) =>
  post<WorkItemView>(
    `/engagements/${engagementId}/work-items/${workItemId}/outputs`,
    { name, format },
  )

/**
 * "Ask [person] to [review / approve / collaborate on]" (ds-promotion 018).
 *
 * By UPN rather than object id, because an ask may legitimately go to somebody
 * who has never signed in - membership binds at first sign-in, so an unbound
 * member has no object id and would otherwise be unaskable.
 */
export const sendAsk = (
  engagementId: string, workItemId: string, upn: string, act: string,
) =>
  post<WorkItemView>(
    `/engagements/${engagementId}/work-items/${workItemId}/asks`,
    { upn, act },
  )

/** Asks someone to look. Never blocks the draft and never gates the publish. */
export const requestOutputReview = (
  engagementId: string, workItemId: string, outputId: string, reviewer: string,
) =>
  post<WorkItemView>(
    `/engagements/${engagementId}/work-items/${workItemId}/outputs/${outputId}/review`,
    { reviewer },
  )

/**
 * The gate. `withheldCount` is omitted rather than sent as 0 — nothing renders
 * an output FROM the graph yet, so there is no filter to count, and "0 internal
 * items excluded" would assert one that never ran.
 */
export const publishOutput = (
  engagementId: string, workItemId: string, outputId: string,
  deliverableId: string, audience: string, withheldCount?: number,
) =>
  post<WorkItemView>(
    `/engagements/${engagementId}/work-items/${workItemId}/outputs/${outputId}/publish`,
    { deliverableId, audience, withheldCount },
  )

/**
 * The engagement's Outputs library.
 *
 * The draft wall is enforced server-side and cannot be reproduced here: this
 * query returns what the caller may see, which is every published output plus
 * the unpublished ones on items they are on. A client-side filter would be the
 * thing C-035 rules out by name.
 */
export const outputsQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'outputs'],
    queryFn: () => apiFetch<OutputLibraryEntry[]>(`/engagements/${engagementId}/outputs`),
  })

/*
  The status report (RFC-0012 §6b and §6d, in RFC-0019's versions).

  Generating is not a record change: it reads the records and stores a version,
  so it is a plain POST with no change set, and the history is all it
  invalidates. A stored version never changes, so once one is read it is never
  read again; the generate call's answer is that version's first read.
*/
export const statusReportsQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'reports'],
    queryFn: () => apiFetch<StatusReportVersionView[]>(`/engagements/${engagementId}/reports`),
  })

export const statusReportVersionQuery = (engagementId: string, reportId: string, version: number) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'reports', reportId, version],
    queryFn: () => apiFetch<StatusReportVersion>(
      `/engagements/${engagementId}/reports/${reportId}/versions/${version}`),
    staleTime: Infinity,
  })

/**
 * One version of the report. With no period named, the server covers the days
 * since the last report, or adds a version to the current one when it already
 * runs to today; the instruction changes the words, never the facts.
 */
export const generateStatusReport = (
  engagementId: string,
  body: { audience: string; orientation: string; instruction?: string | null },
) =>
  post<StatusReportGenerated>(`/engagements/${engagementId}/reports`, {
    audience: body.audience,
    orientation: body.orientation,
    instruction: body.instruction ?? null,
    periodStart: null,
    periodEnd: null,
  })

/**
 * Write the draft's body. C-033's "content authored inside a work item": free,
 * unapproved, appended straight onto the item's stream. Refused on a published
 * output, and the refusal names the way through.
 */
export const draftOutput = (
  engagementId: string, workItemId: string, outputId: string, body: string,
) =>
  post<WorkItemView>(
    `/engagements/${engagementId}/work-items/${workItemId}/outputs/${outputId}/draft`,
    { body },
  )

/**
 * Add a file — something made outside J.E.D.I. that belongs on the record.
 * Multipart, so it does not go through `post`: the body is the file itself.
 */
export const attachOutputFile = async (
  engagementId: string, workItemId: string, outputId: string, file: File,
): Promise<WorkItemView> => {
  const form = new FormData()
  form.append('file', file)
  return postForm<WorkItemView>(
    `/engagements/${engagementId}/work-items/${workItemId}/outputs/${outputId}/file`,
    form,
  )
}

/** The next draft. The published version stays readable — that was the promise. */
export const reviseOutput = (
  engagementId: string, workItemId: string, outputId: string,
) =>
  post<WorkItemView>(
    `/engagements/${engagementId}/work-items/${workItemId}/outputs/${outputId}/revise`,
    {},
  )

/**
 * Give a C·R·A·I·D record an owner, and it DRAFTS.
 *
 * The one verb the read body carries (ruled by Erik, 2026-09-22). It posts a
 * NAME, not an id: the door resolves what it is given against the engagement's
 * own directory, exactly once, and refuses a near miss rather than picking. So
 * the surface offers the names that door will accept, and a refusal is still
 * rendered for the cases a list cannot prevent.
 *
 * NOTHING COMMITS HERE. The endpoint starts a change set and returns its id;
 * the record moves when somebody approves it, which is C-033's single mutation
 * door and the reason this verb was buildable at all. Reassignment replaces
 * rather than refuses: a second owner supersedes the first, and the drafted
 * card names whose name is coming off.
 */
export const setRecordOwner = (engagementId: string, entryId: string, name: string) =>
  post<SetOwnerResponse>(`/engagements/${engagementId}/ledger/${entryId}/owner`, { name })

/**
 * The asks on a record, and sending one (wave 24; Erik, 2026-09-23).
 *
 * THE RECORD'S OWN ROUTE, beside the work item's rather than replacing it. A
 * work item's stream is not a record's and must not become one: C-038 keeps its
 * history with its assignees and author because it carries every draft body, so
 * the generic record endpoints admit the five record families and nothing else.
 *
 * READ SEPARATELY rather than projected on to five lenses, which is the shape
 * the record's history already uses on the same streams: the asks are only ever
 * looked at one record at a time, in the drawer that is open.
 */
export const recordAsksQuery = (engagementId: string, recordId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'record-asks', recordId],
    queryFn: () =>
      apiFetch<RecordAskRow[]>(`/engagements/${engagementId}/records/${recordId}/asks`),
  })

export const sendRecordAsk = (
  engagementId: string, recordId: string, upn: string, act: string,
) =>
  post<RecordAskRow[]>(`/engagements/${engagementId}/records/${recordId}/asks`, { upn, act })

/**
 * Adding somebody to the directory, FIELD BY FIELD.
 *
 * The form used to compose a sentence and send it to `draftChange`, which hands
 * it to a model to parse back into fields. One record came out whose name and
 * role were both the whole sentence. The form already knows which string is the
 * name; this hands it over as one.
 */
export const addStakeholder = (engagementId: string, person: AddStakeholderRequest) =>
  post<AddStakeholderResponse>(`/engagements/${engagementId}/stakeholders`, person)

/** Correcting one field on a directory entry, by name rather than by sentence. */
export const correctStakeholder = (
  engagementId: string, stakeholderId: string, field: string, value: string,
) =>
  post<CorrectStakeholderResponse>(
    `/engagements/${engagementId}/stakeholders/${stakeholderId}/field`, { field, value })

export const pendingSetsQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'pending-sets'],
    queryFn: () =>
      apiFetch<PendingChangeSet[]>(`/engagements/${engagementId}/pending-sets`),
    refetchInterval: 3000,
  })

/** Dismissed drafts are retained as decisions, so an approver can return one
 * to ordinary review without recreating or applying it. */
export const dismissedSetsQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'dismissed-sets'],
    queryFn: () => apiFetch<DismissedHomeProposal[]>(`/engagements/${engagementId}/dismissed-sets`),
  })

export function draftChange(
  engagementId: string,
  intent: string,
  answersQuestionId?: string,
  conversationId?: string,
  scopeRef?: string,
  surface?: string,
  who?: string,
) {
  return apiFetch<DraftChangeResponse>(`/engagements/${engagementId}/changes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      intent,
      answersQuestionId: answersQuestionId ?? null,
      // The chat's per-mount conversation identity (wave-5 step 5) — the
      // server windows memory by it; absent means no memory, deliberately.
      conversationId: conversationId ?? null,
      // The record this conversation is ABOUT (028 §4). A pane labelled `this
      // item` now sends something that names the item, which is the whole
      // reason the pane was not built in wave 17: the label was asserting what
      // the request did not carry. Null everywhere else, and null is the
      // unchanged path.
      scopeRef: scopeRef ?? null,
      // The surface the chat was opened from (wave 23), keyed as the rail keys
      // it. A question with no record in view is answered from its rows.
      surface: surface ?? null,
      // Whose Today that surface is showing (the PM view, ruled 2026-09-22),
      // so the answer is about the people on the screen. Null is your own.
      who: who ?? null,
    }),
  })
}

export function approveSet(engagementId: string, setId: string, body: ApproveSetRequest) {
  return apiFetch<ApproveSetResponse>(`/engagements/${engagementId}/sets/${setId}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

// The guardrail's block-and-ask (wave-6 step 5): one crossing, one
// attributed authorization, reason required — the set stays pending.
export function authorizeGuardrail(
  engagementId: string, setId: string, entryId: string, reason: string,
) {
  return apiFetch<void>(`/engagements/${engagementId}/sets/${setId}/authorize-guardrail`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entryId, reason }),
  })
}

// `subjects` dismisses those alone, each with what is tied to it; the rest of
// the proposal stays pending as a new one, whose id comes back.
export function dismissSet(engagementId: string, setId: string, reason: string, subjects?: string[]) {
  return apiFetch<DismissSetResponse>(`/engagements/${engagementId}/sets/${setId}/dismiss`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(subjects && subjects.length > 0 ? { reason, subjects } : { reason }),
  })
}

export function restoreSet(engagementId: string, setId: string) {
  return apiFetch<RestoreSetResponse>(`/engagements/${engagementId}/sets/${setId}/restore`, {
    method: 'POST',
  })
}

export function needsWorkSet(engagementId: string, setId: string, note?: string) {
  return apiFetch<NeedsWorkResponse>(`/engagements/${engagementId}/sets/${setId}/needs-work`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note: note ?? null }),
  })
}

// Work → All's record rows (ADR-0031 §8): the worked C·R·A·I·D set, read off
// the ledger lens. Same key family as everything engagement-scoped.
export const ledgerQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'ledger'],
    queryFn: () => apiFetch<LedgerEntry[]>(`/engagements/${engagementId}/ledger`),
  })

// Context: the standing conditions (wave-3 step 5).
export const contextQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'context'],
    queryFn: () => apiFetch<ContextEntry[]>(`/engagements/${engagementId}/context`),
  })

// Team: the person/org directory (wave-3 step 5).
/*
  Who is on this engagement. The Assignee column needs it: work items carry
  Entra object ids, and without this the column can only say whether an item is
  assigned rather than to whom.

  Cached for the session rather than per render. Membership changes rarely and
  every row on the surface asks the same question, so refetching per row would
  turn one column into N requests.
*/
export const membersQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'memberships'],
    queryFn: () => apiFetch<EngagementMember[]>(`/engagements/${engagementId}/memberships`),
    staleTime: 5 * 60 * 1000,
  })

export const stakeholdersQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'stakeholders'],
    queryFn: () => apiFetch<Stakeholder[]>(`/engagements/${engagementId}/stakeholders`),
  })

// Indicators: the outcome's measures, rendered on Plan (wave-3 step 5).
export const indicatorsQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'indicators'],
    queryFn: () => apiFetch<Indicator[]>(`/engagements/${engagementId}/indicators`),
  })

/** Each measured Indicator's readings (ADR-0059): the latest, the latest miss, and the recent ones. */
export const indicatorReadingsQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'indicator-readings'],
    queryFn: () => apiFetch<IndicatorReadings[]>(`/engagements/${engagementId}/indicators/readings`),
  })

export const planQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'plan'],
    queryFn: () => apiFetch<PlanNode[]>(`/engagements/${engagementId}/plan`),
  })

/**
 * The critical path to go-live (RFC-0012's 2026-10-20 amendment, §5), read on
 * the server so Plan and the drawer say one thing. Under the plan's key, so an
 * approval that refreshes the plan refreshes the path with it.
 */
/**
 * Named baselines (RFC-0012's 2026-10-20 amendment, §6c): every one, newest
 * first, and the plan one holds, replayed from the log with what differs from
 * today's. Under the engagement's key, so an approval refreshes the comparison.
 */
export const baselinesQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'baselines'],
    queryFn: () => apiFetch<Baseline[]>(`/engagements/${engagementId}/baselines`),
  })

export const baselinePlanQuery = (engagementId: string, baselineId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'baselines', baselineId, 'plan'],
    queryFn: () => apiFetch<BaselinePlan>(`/engagements/${engagementId}/baselines/${baselineId}/plan`),
  })

/** Capture a named baseline of the plan as it stands: a deliberate act by a person who decides on it. */
export function captureBaseline(engagementId: string, name: string) {
  return apiFetch<Baseline>(`/engagements/${engagementId}/baselines`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  })
}

/**
 * The plan items whose dates are older than the evidence J.E.D.I. holds
 * (RFC-0012's 2026-10-20 amendment, §3): one predicate for Plan's banner, its
 * row marks, its filter and the rail's count.
 */
export const staleQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'plan', 'stale'],
    queryFn: () => apiFetch<StaleItem[]>(`/engagements/${engagementId}/plan/stale`),
  })

export const criticalPathQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'plan', 'critical-path'],
    queryFn: () => apiFetch<CriticalPath>(`/engagements/${engagementId}/plan/critical-path`),
  })

// Surveillance's computed read (wave-13). Its own key rather than a slice of
// the plan's: the topbar's health tile and the tray both consume it, and a
// derived read that hid inside another query would be invalidated by accident
// rather than on purpose.
export const outcomeWatchQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'outcome-watch'],
    queryFn: () => apiFetch<WatchedOutcome[]>(`/engagements/${engagementId}/outcome-watch`),
  })

// The nudge tray. The badge and the tray's contents are ONE query, the same
// binding 010/026 put on the rail counts: a bell that says 2 and a tray that
// shows something else is worse than no bell.
export const nudgesQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'nudges'],
    queryFn: () => apiFetch<NudgeTray>(`/engagements/${engagementId}/nudges`),
  })

export const disposeNudge = (
  engagementId: string,
  nudgeId: string,
  body: { kind: 'snoozed' | 'dismissed'; reason?: string; until?: string },
) =>
  apiFetch<void>(`/engagements/${engagementId}/nudges/${encodeURIComponent(nudgeId)}/dispose`, {
    method: 'POST',
    // SAY WHAT THE BODY IS. `fetch` with a string body and no headers sends
    // `text/plain;charset=UTF-8`, which ASP.NET will not bind a JSON parameter
    // from: it answers 415 before the handler runs, so nothing is written. This
    // was the only call in the file sending JSON without it, and it is why
    // Snooze and Dismiss did nothing on dev. Every other body-carrying call
    // here already had it; the document upload deliberately does not, because
    // it sends FormData and the browser owns that boundary.
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

// Stamping a surface open is what makes the badge mean "new since you looked".
export const markSurfaceOpened = (engagementId: string, surfaceId: string) =>
  apiFetch<void>(`/engagements/${engagementId}/surfaces/${surfaceId}/opened`, { method: 'POST' })

// The Runs surface (wave-5 step 6): the traces the substrate has persisted
// since wave 2, given a door — internal eyes on the instrument. The list is
// summaries; the drill fetches the full trace on demand.
export const runsQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'runs'],
    queryFn: () => apiFetch<RunSummary[]>(`/engagements/${engagementId}/runs`),
  })

export const runDetailQuery = (engagementId: string, runId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'runs', runId],
    queryFn: () => apiFetch<AgentRunTrace>(`/engagements/${engagementId}/runs/${runId}`),
  })

// Persisted eval runs: platform-plane like the directory — the instrument's
// history, not engagement content, so the key carries no engagement.
export const evalReportsQuery = queryOptions({
  queryKey: ['eval-reports'],
  queryFn: () => apiFetch<EvalRunReport[]>('/eval-reports'),
})

// ServiceNow's documentation (ADR-0054): the releases JEDI answers from, and a search over one,
// for the people who run the platform. Platform plane like the eval reports, so the keys carry no
// engagement; a 503 from either means the documentation is unavailable, never that it is empty.
//
// NOT RETRIED. The client's policy reads a 503 as the API waking and waits a minute for it, but
// this page opens only once the API is awake, and the API itself waits out the documentation
// service's cold start. A 503 here is the answer, so it is shown at once.
export const productReleasesQuery = queryOptions({
  queryKey: ['product-documentation', 'releases'],
  queryFn: () => apiFetch<ProductCatalog>('/product-documentation/releases'),
  retry: false,
})

export const productSearchQuery = (search: ProductSearchRequest) =>
  queryOptions({
    queryKey: ['product-documentation', 'search', search],
    queryFn: () =>
      apiFetch<ProductSearchResult>('/product-documentation/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(search),
      }),
    retry: false,
  })

export const recordHistoryQuery = (engagementId: string, recordId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'record', recordId, 'history'],
    queryFn: () =>
      apiFetch<HistoryLine[]>(`/engagements/${engagementId}/records/${recordId}/history`),
  })

// Home's ask-the-PM rows (B-1: J.E.D.I.-initiated arrivals land on the actor
// surface): intake's honest "I couldn't classify this". Same polling posture
// as the pending sets — questions arrive off the worker's queue.
export const intakeQuestionsQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'intake-questions'],
    queryFn: () =>
      apiFetch<PendingIntakeQuestion[]>(`/engagements/${engagementId}/intake-questions`),
    refetchInterval: 3000,
  })

export function resolveIntakeQuestion(engagementId: string, questionId: string, note?: string) {
  return apiFetch<ResolveQuestionResponse>(
    `/engagements/${engagementId}/intake-questions/${questionId}/resolve`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note ?? null }),
    })
}

// The document library (wave-7 step 4): every document's live ingest state,
// read off the DocumentRow projection. Polling is a UI READ, not a sync —
// the indexing path stays push-only (recorded ruling 4's constraint), and
// this is the same 3s cadence the pending sets already use for the same
// reason: things arrive off the worker's queue while the surface is open.
export const documentsQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'documents'],
    queryFn: () => apiFetch<DocumentRow[]>(`/engagements/${engagementId}/documents`),
    refetchInterval: 3000,
  })

// `route` false reads the document without routing it (Erik's demo-environment
// rulings, 2026-10-22): extracted, indexed and kept, and nothing proposed from
// it. The field is sent only to say "false"; absent means routed, as the API
// reads it, which is what every upload before it meant.
export function uploadDocument(engagementId: string, file: File, route = true) {
  const form = new FormData()
  form.append('file', file)
  if (!route) form.append('route', 'false')
  return apiFetch<UploadResponse>(`/engagements/${engagementId}/documents`, {
    method: 'POST',
    body: form,
  })
}

// Cold start (wave-12). One call per generation-order layer, in order, because
// the order is a dependency rather than a preference: the plan cannot be read
// until the baseline outcomes exist for its deliverables to enable. A refused
// layer comes back 200 with a `gate` sentence rather than an error status —
// the refusal is content a person reads on the stage they are standing on.
export type ColdStartLayer = 'outcomes' | 'people' | 'context' | 'plan'

export function draftColdStartLayer(
  engagementId: string,
  documentId: string,
  layer: ColdStartLayer,
) {
  return apiFetch<ColdStartDraftResponse>(`/engagements/${engagementId}/cold-start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ documentId, layer, version: 1 }),
  })
}

// Where cold start stands, from what the engagement holds: each layer's last
// read, its document and what still waits. The flow reopens on it.
export const coldStartProgressQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['eng', engagementId, 'cold-start'],
    queryFn: () => apiFetch<ColdStartProgress>(`/engagements/${engagementId}/cold-start`),
  })

// Mark as agreed (ADR-0042): drafts the outcome's agreement for an approver
// and writes nothing. Pressed again, it answers with the one already waiting.
export function agreeOutcome(engagementId: string, outcomeId: string) {
  return apiFetch<AgreeOutcomeResponse>(`/engagements/${engagementId}/outcomes/${outcomeId}/agree`, {
    method: 'POST',
  })
}

// ---------------------------------------------------------------------------
// Administration (ADR-0041). Platform-plane: these are gated on the
// EngagementAdmin app role at the API, which is where the boundary is. The
// admin route's own check is cosmetic and says so.
// ---------------------------------------------------------------------------

export type CreateEngagementBody = {
  id: string
  name: string
  organizationId: string
  organizationName: string
  customerId: string
  customerName: string
  product: string
  outcomeName?: string | null
  outcomeDescription?: string | null
  release?: string | null
  upgradingTo?: string | null
}

export function createEngagement(body: CreateEngagementBody) {
  return apiFetch<CreateEngagementResponse>('/engagements', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export function renameEngagement(
  engagementId: string,
  /*
    THE THREE ARE A DIFFERENT AXIS EACH (ADR-0025): the client's parent, the
    unit served inside it, and the platform the work is delivered on. None of
    them is who is DELIVERING it, which is one value for the environment and
    has its own door.
  */
  body: {
    name?: string | null
    organizationName?: string | null
    customerName?: string | null
    product?: string | null
  },
) {
  return apiFetch<EngagementProfile>(`/engagements/${engagementId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export function grantMembership(engagementId: string, upn: string, role: string) {
  return apiFetch<MembershipGrantResponse>(`/engagements/${engagementId}/memberships`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ upn, role }),
  })
}

/*
  The other half (RAID #402, ADR-0041 §1). Refuses with 404 when the membership
  is not there and 409 + `says` when it is the last approver, so the caller
  renders J.E.D.I.'s words rather than a status code.

  The UPN goes in the PATH and is encoded here. It is an email address, so `+`
  and other sub-address characters are legal in it and would otherwise be
  decoded as something else on the way in.
*/
export function revokeMembership(engagementId: string, upn: string) {
  return apiFetch<MembershipRevokeResponse>(
    `/engagements/${engagementId}/memberships/${encodeURIComponent(upn)}`,
    { method: 'DELETE' },
  )
}

/*
  Changing what somebody may do without removing them. Its own call rather than
  a second grant, because a grant also CREATES a membership: sent from a list
  gone stale, it would let back in somebody another administrator had just
  removed. This one refuses with 404 when they are not on the engagement, and
  with 409 + `says` when they are the last person who can decide.
*/
/**
 * Who is delivering the work: ADR-0025's Company tier, one per environment.
 *
 * NOT THE ENGAGEMENT'S `organizationName`, and the difference is the whole
 * reason this exists. That field is the CLIENT's parent (Johns Hopkins, or a
 * prime such as ServiceNow); this is us. A surface that wants to say "our own
 * side" reads this, and for want of it one of them read the other.
 *
 * Cached for the session. An environment's own name changes about as often as
 * the environment does.
 */
export const companyQuery = queryOptions({
  queryKey: ['company'],
  queryFn: () => apiFetch<Company>('/company'),
  staleTime: 10 * 60_000,
})

/**
 * The products an engagement can be delivered on, with their releases oldest first and which of
 * them JEDI has documentation for (RFC-0013's 2026-10-07 amendment). Vocabulary that moves only
 * with a deploy, so cached for the session like the company's name.
 */
export const productsQuery = queryOptions({
  queryKey: ['products'],
  queryFn: () => apiFetch<ProductOption[]>('/products'),
  staleTime: 10 * 60_000,
})

/** The time zone the engagement's customer works in (Erik, 2026-10-09): scheduled work runs off-hours in it. */
export function setEngagementTimeZone(engagementId: string, timeZone: string) {
  return apiFetch<EngagementProfile>(`/engagements/${engagementId}/time-zone`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ timeZone }),
  })
}

/**
 * The engagement's phases, set whole (ADR-0061): in order, a phase being added sent without an id, and
 * a phase left out removed. The API refuses, in a sentence, a phase anything still sits in.
 */
export function setEngagementPhases(engagementId: string, phases: EngagementPhaseInput[]) {
  return apiFetch<EngagementProfile>(`/engagements/${engagementId}/phases`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phases }),
  })
}

/** The engagement's task categories, set whole (ADR-0030's 2026-10-21 amendment); an empty list means none. */
export function setEngagementCategories(engagementId: string, categories: EngagementCategoryInput[]) {
  return apiFetch<EngagementProfile>(`/engagements/${engagementId}/categories`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ categories }),
  })
}

/** What the engagement is delivered on, set whole: leaving the upgrade empty clears it. */
export function setEngagementPlatform(engagementId: string, body: EngagementPlatformRequest) {
  return apiFetch<EngagementProfile>(`/engagements/${engagementId}/platform`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export function setCompany(name: string) {
  return apiFetch<Company>('/company', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  })
}

/**
 * Tying a membership to its directory entry (ADR-0044 §4).
 *
 * AN ACT, NEVER A MATCH, which is why the SPA has to make it deliberately: a
 * membership is born from an address and a directory entry from a document, the
 * two populations share no field, and a guess attributes somebody else's
 * records to the wrong person silently. The add form performs it at the one
 * moment the guess is not a guess, because the person adding just picked them.
 */
export function bindMembershipToStakeholder(
  engagementId: string, upn: string, stakeholderId: string,
) {
  return apiFetch<MembershipBindResponse>(
    `/engagements/${engagementId}/memberships/${encodeURIComponent(upn)}/stakeholder`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stakeholderId }),
    },
  )
}

export function changeMembershipRole(engagementId: string, upn: string, role: string) {
  return apiFetch<MembershipRoleResponse>(
    `/engagements/${engagementId}/memberships/${encodeURIComponent(upn)}/role`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    },
  )
}

export const engagementProfileQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['engagement-profile', engagementId],
    queryFn: () => apiFetch<EngagementProfile>(`/engagements/${engagementId}/profile`),
  })

/*
  Settings › Integrations (plan step 3.5): the engagement's connections to its customer's ServiceNow
  instances. Every member reads them; a steward or an EngagementAdmin changes them, and the API says
  so to anybody else. A credential goes out in a request and never comes back: no answer holds one.
  While a connection is being checked, the list reads again every few seconds until the check ends.
*/
const connectionsPath = (engagementId: string) => `/engagements/${engagementId}/servicenow/connections`

export const serviceNowConnectionsQuery = (engagementId: string) =>
  queryOptions({
    queryKey: ['servicenow-connections', engagementId],
    queryFn: () => apiFetch<ServiceNowConnection[]>(connectionsPath(engagementId)),
    // A check takes seconds and reading the tables minutes, so each is waited for at its own pace.
    refetchInterval: (query) =>
      query.state.data?.some((c) => c.verification === 'pending')
        ? 3_000
        : query.state.data?.some((c) => c.inventory === 'running')
          ? 10_000
          : false,
  })

export const serviceNowIdentityQuery = (engagementId: string, connectionId: string) =>
  queryOptions({
    queryKey: ['servicenow-identity', engagementId, connectionId],
    queryFn: () => apiFetch<ServiceNowIdentity>(`${connectionsPath(engagementId)}/${connectionId}/identity`),
  })

/** A search of the instance profile: by name, label or field, narrowed to the tables with configuration, ordered, a page at a time. */
export type TableSearch = { q: string; configured: boolean; order: 'name' | 'configuration'; take: number }

/** `asOf` is when the profile was last read: a new read is a new answer, so the tables read again. */
export const serviceNowTablesQuery = (engagementId: string, connectionId: string, search: TableSearch, asOf: string) =>
  queryOptions({
    queryKey: ['servicenow-tables', engagementId, connectionId, search, asOf],
    queryFn: () => {
      const params = new URLSearchParams({ order: search.order, take: String(search.take) })
      if (search.q.trim()) params.set('q', search.q.trim())
      if (search.configured) params.set('configured', 'true')
      return apiFetch<ServiceNowTablePage>(`${connectionsPath(engagementId)}/${connectionId}/tables?${params}`)
    },
    // A new search keeps the last answer on screen until its own arrives.
    placeholderData: keepPreviousData,
  })

export const serviceNowTableQuery = (engagementId: string, connectionId: string, name: string, asOf: string) =>
  queryOptions({
    queryKey: ['servicenow-table', engagementId, connectionId, name, asOf],
    queryFn: () => apiFetch<ServiceNowTableDetail>(`${connectionsPath(engagementId)}/${connectionId}/tables/${encodeURIComponent(name)}`),
  })

const sendJson = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
})

export const connectServiceNow = (engagementId: string, body: ServiceNowConnectionRequest) =>
  apiFetch<ServiceNowConnection>(connectionsPath(engagementId), sendJson('POST', body))

export const replaceServiceNowCredential = (engagementId: string, connectionId: string, body: ServiceNowCredentialRequest) =>
  apiFetch<ServiceNowConnection>(`${connectionsPath(engagementId)}/${connectionId}/credential`, sendJson('PUT', body))

export const verifyServiceNow = (engagementId: string, connectionId: string) =>
  apiFetch<ServiceNowConnection>(`${connectionsPath(engagementId)}/${connectionId}/verify`, { method: 'POST' })

export const readServiceNowTables = (engagementId: string, connectionId: string) =>
  apiFetch<ServiceNowConnection>(`${connectionsPath(engagementId)}/${connectionId}/inventory`, { method: 'POST' })

export const scheduleServiceNow = (engagementId: string, connectionId: string, pass: string, body: ServiceNowScheduleRequest) =>
  apiFetch<ServiceNowConnection>(`${connectionsPath(engagementId)}/${connectionId}/schedules/${pass}`, sendJson('PUT', body))

export const pauseServiceNow = (engagementId: string, connectionId: string) =>
  apiFetch<ServiceNowConnection>(`${connectionsPath(engagementId)}/${connectionId}/pause`, sendJson('POST', { reason: null }))

export const resumeServiceNow = (engagementId: string, connectionId: string) =>
  apiFetch<ServiceNowConnection>(`${connectionsPath(engagementId)}/${connectionId}/resume`, { method: 'POST' })

export const removeServiceNow = (engagementId: string, connectionId: string) =>
  apiFetch<void>(`${connectionsPath(engagementId)}/${connectionId}`, { method: 'DELETE' })

/**
 * Feedback about J.E.D.I. itself (ADR-0065): `POST /feedback`. Not engagement
 * scoped, because it is about the product; the engagement travels as a
 * reference the issue names, never as a key anything is read under.
 */

export function sendFeedback(body: FeedbackBody) {
  return apiFetch<FeedbackReceipt>('/feedback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}
